import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { APP_CONFIG } from '@cfg/env/app.config';
import { TemplateRepository, ExamCatalogItem } from '@service/templates/template.repository';
import {
  Template,
  TemplateVersionDetail,
  TemplateVersion,
  SaveVersionRequest,
  CopyTemplateRequest
} from '@model/templates';

const handleError = (err: unknown) =>
  throwError(() => new Error((err as { error?: { message?: string }; message?: string })?.error?.message ?? (err as Error)?.message ?? 'Error desconocido'));

@Injectable({ providedIn: 'root' })
export class HttpTemplateRepository extends TemplateRepository {
  private readonly httpClient = inject(HttpClient);
  private readonly appConfig = inject(APP_CONFIG);

  private get baseUrl(): string {
    const config = this.appConfig.validationResults as
      | { baseUrl?: string; context: string }
      | undefined;
    const base = config?.baseUrl ?? '';
    const context = config?.context ?? '/validacion_resultados';
    return `${base}${context}`;
  }

  override getExamCatalog(): Observable<ExamCatalogItem[]> {
    return this.httpClient.get<ExamCatalogItem[]>(
      `${this.baseUrl}/exam-catalog`
    ).pipe(catchError(handleError));
  }

  override searchExams(name: string): Observable<ExamCatalogItem[]> {
    const params = new HttpParams().set('name', name);
    return this.httpClient.get<ExamCatalogItem[]>(
      `${this.baseUrl}/exam-catalog`,
      { params }
    ).pipe(catchError(handleError));
  }

  override findAll(examId?: string, resultTypeId?: string, active = true): Observable<Template[]> {
    let params = new HttpParams();
    if (examId) {
      params = params.set('examId', examId);
    }
    if (resultTypeId) {
      params = params.set('resultTypeId', resultTypeId);
    }
    if (active !== undefined) {
      params = params.set('active', active.toString());
    }
    return this.httpClient.get<Template[]>(`${this.baseUrl}/templates`, { params }).pipe(catchError(handleError));
  }

  override findById(id: string): Observable<Template> {
    return this.httpClient.get<Template>(`${this.baseUrl}/templates/${id}`).pipe(catchError(handleError));
  }

  override create(template: Partial<Template>): Observable<Template> {
    return this.httpClient.post<Template>(`${this.baseUrl}/templates`, template).pipe(catchError(handleError));
  }

  override update(id: string, template: Partial<Template>): Observable<Template> {
    return this.httpClient.put<Template>(`${this.baseUrl}/templates/${id}`, template).pipe(catchError(handleError));
  }

  override deactivate(id: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/templates/${id}`).pipe(catchError(handleError));
  }

  override copy(id: string, request: CopyTemplateRequest): Observable<Template> {
    return this.httpClient.post<Template>(`${this.baseUrl}/templates/${id}/copy`, request).pipe(catchError(handleError));
  }

  override getVersion(templateId: string, versionId: string): Observable<TemplateVersionDetail> {
    return this.httpClient.get<TemplateVersionDetail>(
      `${this.baseUrl}/templates/${templateId}/versions/${versionId}`
    ).pipe(catchError(handleError));
  }

  override getVersions(templateId: string): Observable<TemplateVersion[]> {
    return this.httpClient.get<TemplateVersion[]>(
      `${this.baseUrl}/templates/${templateId}/versions`
    ).pipe(catchError(handleError));
  }

  override saveVersion(
    templateId: string,
    versionId: string,
    request: SaveVersionRequest
  ): Observable<TemplateVersionDetail> {
    return this.httpClient.put<TemplateVersionDetail>(
      `${this.baseUrl}/templates/${templateId}/versions/${versionId}`,
      request
    ).pipe(catchError(handleError));
  }

  override publishVersion(
    templateId: string,
    versionId: string
  ): Observable<TemplateVersionDetail> {
    return this.httpClient.post<TemplateVersionDetail>(
      `${this.baseUrl}/templates/${templateId}/versions/${versionId}/publish`,
      {}
    ).pipe(catchError(handleError));
  }

  override createVersion(templateId: string): Observable<TemplateVersionDetail> {
    return this.httpClient.post<TemplateVersionDetail>(
      `${this.baseUrl}/templates/${templateId}/versions`,
      {}
    ).pipe(catchError(handleError));
  }
}
